import { get } from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";

import type { ClientConfig, NotificationConfig } from "@platform/dmv-api";
import { Badge } from "@shared/ui";

import { updateClientsConfig } from "../../lib/automatic";
import ButtonPrimary from "../ButtonPrimary";
import NotificationModal from "./NotificationModal";
import SettingsSection from "./SettingsSection";

const NotificationsSection = ({
  id,
  config,
  clientId,
}: {
  id?: string;
  config: ClientConfig | null;
  clientId: string;
}) => {
  const [notifications, setNotifications] = useState<NotificationConfig[]>(
    get(config, "notificationConfig", [])
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleOpenCreateModal = () => {
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (index: number) => {
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIndex(null);
  };

  const saveNotificationsToApi = async (
    updatedNotifications: NotificationConfig[],
    successMessage: string
  ) => {
    const loadingToast = toast.loading("Saving...");
    const [, error] = await updateClientsConfig(clientId, {
      notificationConfig: updatedNotifications,
    });
    toast.dismiss(loadingToast);
    if (error) {
      let errorMessageParsed = "Oops! Something went wrong. Please try again.";
      switch (get(error, "code")) {
        case 400:
          const errorMessage = get(error, "message");
          if (errorMessage?.includes("duplicate")) {
            errorMessageParsed = "Duplicate notification already exists.";
          }
          break
        default:
          errorMessageParsed = "Oops! Something went wrong. Please try again.";
      }
      toast.error(errorMessageParsed);
      return false;
    }
    toast.success(successMessage);
    return true;
  };

  const handleSaveNotification = async (notification: NotificationConfig) => {
    const isEdit = editingIndex !== null;
    let updated: NotificationConfig[];
    if (isEdit) {
      updated = [...notifications];
      updated[editingIndex] = notification;
    } else {
      updated = [...notifications, notification];
    }
    const success = await saveNotificationsToApi(
      updated,
      isEdit ? "Notification updated" : "Notification added"
    );
    if (success) {
      setNotifications(updated);
    }
  };

  const handleDeleteNotification = async (index: number) => {
    const updated = notifications.filter((_, i) => i !== index);
    const success = await saveNotificationsToApi(updated, "Notification deleted");
    if (success) {
      setNotifications(updated);
    }
  };

  const editingNotification =
    editingIndex !== null ? notifications[editingIndex] : null;

  return (
    <>
      <SettingsSection
        id={id}
        title="Notifications"
        subtitle="Configure notifications for events in your Axle integration."
        headerAction={
          <ButtonPrimary
            text="Add Notification"
            onClick={handleOpenCreateModal}
            icon={undefined}
            className=""
          />
        }
      >
        {notifications.length === 0 ? (
          <p className="text-sm text-gray-500">No notifications configured.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="pb-3 pr-6 font-medium text-gray-700">Channel</th>
                  <th className="pb-3 pr-6 font-medium text-gray-700">Type</th>
                  <th className="pb-3 pr-6 font-medium text-gray-700">Status</th>
                  <th className="pb-3 pr-6 font-medium text-gray-700">Locations</th>
                  <th className="pb-3 font-medium text-gray-700 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {notifications.map((notification, index) => (
                  <tr key={index} className="hover:bg-gray-50 group align-top">
                    <td className="py-3 pr-6 capitalize">
                      {notification.channel ?? "—"}
                    </td>
                    <td className="py-3 pr-6">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {notification.type ?? "—"}
                      </code>
                    </td>
                    <td className="py-3 pr-6">
                      <Badge status={notification.enabled ? "active" : "inactive"} />
                    </td>
                    <td className="py-3 pr-6">
                      {notification.locations?.length ? (
                        <div className="flex flex-col gap-y-1">
                          {notification.locations.map((location, locIndex) => (
                            <code
                              key={locIndex}
                              className="text-xs text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded break-all font-mono"
                            >
                              {location}
                            </code>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 align-middle">
                      <div className="flex gap-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleOpenEditModal(index)}
                          className="text-sm text-gray-500 hover:text-black"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteNotification(index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsSection>

      <NotificationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveNotification}
        initialData={editingNotification}
      />
    </>
  );
};

export default NotificationsSection;
